/**
 * The panel's ONE host transport seam (outlook-orachat-uplift s4, proof K2).
 *
 * Every browser request the chat runtime makes (ask / continue / abort,
 * history, sessions, prefs, plan, STT, the realtime mint, speech ack, the
 * hands-free bridge, documents and the vault) goes through `oraChatFetch`.
 * The call sites keep the Remix route literals they always used; the
 * transport decides where they really go.
 *
 * Ora web (and quote-compare) install nothing: `oraChatFetch` then calls the
 * platform `fetch` with the exact same arguments and arity as the call it
 * replaced, so their request path is unchanged (design C1).
 *
 * A host that cannot ride the Remix cookie session (the Outlook add-in: a
 * bearer instead of a cookie, no Remix resource routes) installs its own
 * transport once at boot. The transport never retries (C9): ask is not
 * idempotent.
 *
 * Nothing here may import from `apps/` — the seam is the panel's, the hosts
 * plug into it.
 */

export type OraChatTransport = (
  input: RequestInfo | URL,
  init?: RequestInit,
) => Promise<Response>;

let hostTransport: OraChatTransport | null = null;
let hostAvatarSrc: string | null = null;

/** What a host mic press settled as. The panel never classifies this itself. */
export type HostMicStatus =
  | "available"
  | "needs_reload"
  | "blocked"
  | "unavailable";

export interface HostMicVerdict {
  status: HostMicStatus;
  reason: string | null;
  hostDialogPending: boolean;
}

/**
 * Optional microphone owner (the Outlook add-in). Ora web installs nothing:
 * the getters stay null and the panel keeps today's mic path.
 */
export interface OraChatVoiceHost {
  acquireMicrophone(): Promise<HostMicVerdict>;
  /** `"relay"` skips the browser SpeechRecognition branch for dictation. */
  stt?: "relay";
}

let voiceHost: OraChatVoiceHost | null = null;
let sessionStore: Storage | null = null;

/** Install (or clear, with `null`) the host microphone. */
export function setOraChatVoiceHost(host: OraChatVoiceHost | null): void {
  voiceHost = host;
}

export function getOraChatVoiceHost(): OraChatVoiceHost | null {
  return voiceHost;
}

/**
 * Install (or clear, with `null`) the panel's session cache. Ora web installs
 * nothing and keeps `window.sessionStorage`. A host that must not persist
 * chat history across a permission reload installs an in-memory store.
 */
export function setOraChatSessionStore(store: Storage | null): void {
  sessionStore = store;
}

export function getOraChatSessionStore(): Storage | null {
  return sessionStore;
}

/** Install (or clear, with `null`) the host transport. */
export function setOraChatTransport(transport: OraChatTransport | null): void {
  hostTransport = transport;
}

/**
 * The panel's fetch. With no host transport installed it is the platform
 * `fetch`, looked up at CALL time (tests replace it per test) and called
 * with the same arity: a one-argument call stays one-argument.
 */
export function oraChatFetch(
  input: RequestInfo | URL,
  init?: RequestInit,
): Promise<Response> {
  if (hostTransport) return hostTransport(input, init);
  return init === undefined
    ? globalThis.fetch(input)
    : globalThis.fetch(input, init);
}

/**
 * Install (or clear, with `null`) the host's avatar image source. Ora web
 * installs nothing and keeps its root-relative `/firefly.svg`; a host served
 * from another origin installs its own bundled asset URL (design C8).
 */
export function setOraChatAvatarSrc(src: string | null): void {
  hostAvatarSrc = src;
}

export function getOraChatAvatarSrc(): string | null {
  return hostAvatarSrc;
}
